import React, { useState, useEffect } from "react";
import { Briefcase } from "lucide-react";
import { apiClient } from "../../utils/apiClient/index.ts";
import { useAlert } from "../../hooks/useAlert";
import type {
  CreateCaseDto,
  Organization,
  CaseStatus,
  CasePriority,
} from "../../types";
import Button from "../../components/common/ui/button/Button";
import Label from "../../components/common/form/Label";
import Input from "../../components/common/form/input/InputField";
import Breadcrumb from "../../components/common/common/Breadcrumb";
import { useOrg } from "../../contexts/OrgContext.tsx";

export const CreateCasePage: React.FC = () => {
  const { setAlert } = useAlert();

  const { orgId, navigateOrg } = useOrg();

  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(false);

  // Form state
  const [caseTitle, setCaseTitle] = useState("");
  const [caseDescription, setCaseDescription] = useState("");
  const [casePriority, setCasePriority] = useState<CasePriority>(
    "medium" as CasePriority,
  );

  useEffect(() => {
    if (orgId) {
      loadOrganization();
    }
  }, [orgId]);

  const loadOrganization = async () => {
    if (!orgId) return;

    try {
      const orgs = await apiClient.listOrganizations();
      const org = orgs.find((o) => o.id === orgId);
      if (org) {
        setOrganization(org);
      } else {
        setAlert({
          variant: "error",
          title: "Error",
          message: "Organization not found",
        });
        navigateOrg("/cases");
      }
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error
            ? error.message
            : "Failed to load organization",
      });
      navigateOrg("/cases");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization) return;

    setLoading(true);
    try {
      const caseData: CreateCaseDto = {
        title: caseTitle,
        description: caseDescription,
        organizationId: organization.id,
        priority: casePriority,
        status: "open" as CaseStatus,
      };

      await apiClient.createCase(caseData);
      setAlert({
        variant: "success",
        title: "Success",
        message: "Case created successfully",
      });
      navigateOrg("/cases");
    } catch (error) {
      setAlert({
        variant: "error",
        title: "Error",
        message:
          error instanceof Error ? error.message : "Failed to create case",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigateOrg("/cases");
  };

  return (
    <div className="space-y-6">
      <Breadcrumb
        items={[{ label: "Cases", path: "/cases" }, { label: "New Case" }]}
      />

      <div className="flex items-center gap-3">
        <Briefcase size={32} className="text-brand-500" />
        <div>
          <h1 className="text-title-md2 font-semibold text-black dark:text-white">
            Create New Case
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {organization
              ? `Creating case for ${organization.name}`
              : "Loading..."}
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label>
              Case Title <span className="text-error-500">*</span>
            </Label>
            <Input
              type="text"
              value={caseTitle}
              onChange={(e) => setCaseTitle(e.target.value)}
              required
              placeholder="Enter case title"
            />
          </div>

          <div>
            <Label>Description</Label>
            <textarea
              value={caseDescription}
              onChange={(e) => setCaseDescription(e.target.value)}
              placeholder="Optional case description"
              rows={6}
              className="h-auto w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 placeholder:text-gray-400 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90 dark:placeholder:text-white/30"
            />
          </div>

          <div>
            <Label>
              Priority <span className="text-error-500">*</span>
            </Label>
            <select
              value={casePriority}
              onChange={(e) => setCasePriority(e.target.value as CasePriority)}
              className="h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 focus:border-brand-300 focus:outline-hidden focus:ring-3 focus:ring-brand-500/10 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={loading || !organization}>
              {loading ? "Creating..." : "Create Case"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
